// Tipos de «Mis clientes» (`/collections/customers`). Describen la FILA QUE SE PINTA, no un DTO del
// silo: la cobranza todavía no expone endpoints y los datos salen de `fixtures.ts`.
import { ClientLevelEnum } from '@fiado/type-kit/bin/loanScoring/enums/ClientLevelEnum';

/** El nivel del cliente. `null` es AUSENCIA de nivel — el chip «Sin» del mockup. */
export type CustomerTier = ClientLevelEnum | null;

/** Por dónde se contactó al cliente (`canal` del mockup). */
export type ContactChannel = 'call' | 'sms' | 'chat' | 'whatsapp' | 'voice_blast';

/** En qué terminó el intento (`resultado` del mockup). */
export type ContactOutcome =
    | 'no_answer'
    | 'talked'
    | 'promise'
    | 'wrong_number'
    | 'voicemail'
    | 'sent';

/** Por dónde se comprometió a pagar, y por dónde entró el pago. */
export type PaymentChannel = 'oxxo' | 'store' | 'app';

export type PromiseStatus = 'active' | 'kept' | 'broken';

export const CONTACT_CHANNEL_LABEL: Record<ContactChannel, string> = {
    call: 'llamada',
    sms: 'sms',
    chat: 'chat',
    whatsapp: 'whatsapp',
    voice_blast: 'voz blaster',
};

export const CONTACT_OUTCOME_LABEL: Record<ContactOutcome, string> = {
    no_answer: 'no contesta',
    talked: 'hablo',
    promise: 'promesa',
    wrong_number: 'num equivocado',
    voicemail: 'buzon',
    sent: 'enviado',
};

export const PAYMENT_CHANNEL_LABEL: Record<PaymentChannel, string> = {
    oxxo: 'oxxo',
    store: 'tienda',
    app: 'app',
};

export const PROMISE_STATUS_LABEL: Record<PromiseStatus, string> = {
    active: 'Activo',
    kept: 'Cumplido',
    broken: 'Incumplido',
};

/** Un crédito del cliente, tal como lo pinta la pestaña «Créditos». */
export interface CollectorCreditRow {
    id: string;
    /** Marca y modelo van desnormalizados: la fila los pinta, no resuelve un catálogo. */
    deviceBrand: string;
    deviceModel: string;
    paidInstallments: number;
    totalInstallments: number;
    arrearsDays: number;
    arrearsCents: number;
}

/** Un intento de contacto (`historial` del mockup). */
export interface CustomerContact {
    /** `YYYY-MM-DDTHH:mm` — sello local del piso de cobranza, sin zona: el fixture no es del wire. */
    at: string;
    creditId: string;
    channel: ContactChannel;
    outcome: ContactOutcome;
    note: string;
}

/** Un compromiso de pago (`compromisos` del mockup). */
export interface CustomerPromise {
    id: string;
    creditId: string;
    /** Día `YYYY-MM-DD` en que vence. */
    day: string;
    amountCents: number;
    channel: PaymentChannel;
    status: PromiseStatus;
}

/** Un pago recibido (`pagos` del mockup). */
export interface CustomerPayment {
    id: string;
    creditId: string;
    at: string;
    amountCents: number;
    channel: PaymentChannel;
}

/** La fila de la tabla, con todo lo que las cinco pestañas del detalle necesitan. */
export interface CollectorCustomerRow {
    id: string;
    name: string;
    phone: string | null;
    curp: string | null;
    tier: CustomerTier;
    monthlyIncomeCents: number | null;
    altScore: number | null;
    credits: CollectorCreditRow[];
    /** La peor mora entre sus créditos: es la que ordena la tabla. */
    worstArrearsDays: number;
    totalArrearsCents: number;
    activePromise: CustomerPromise | null;
    brokenPromise: CustomerPromise | null;
    hasPromiseToday: boolean;
    hasPromiseTomorrow: boolean;
    lastContact: CustomerContact | null;
    /** `NO_CONTACT_DAYS` cuando nunca se le contactó: no es un cero, es un nunca. */
    daysWithoutContact: number;
    attempts48h: number;
    promises: CustomerPromise[];
    contacts: CustomerContact[];
    payments: CustomerPayment[];
}

/** Opción «sin nivel» de los chips: un `null` no sirve de key ni de valor de formulario. */
export const NO_TIER_FILTER = 'none';
export type TierFilterValue = ClientLevelEnum | typeof NO_TIER_FILTER;

/** Valor «Todos» de los tres selects del panel. */
export const ANY_FILTER = 'any';

export type ArrearsFilter = 'any' | 'current' | '1-7' | '8-30' | '31-60' | '61-90' | '90+';
export type PromiseFilter = 'any' | 'today' | 'tomorrow' | 'active' | 'broken' | 'none';
export type ContactFilter = 'any' | 'today' | '24h' | '3d' | '7d';

export interface CustomersFilters {
    search: string;
    tiers: TierFilterValue[];
    arrears: ArrearsFilter;
    promise: PromiseFilter;
    contact: ContactFilter;
}

export function emptyCustomersFilters(): CustomersFilters {
    return {
        search: '',
        tiers: [],
        arrears: ANY_FILTER,
        promise: ANY_FILTER,
        contact: ANY_FILTER,
    };
}

export type CustomerStatKey = 'all' | 'arrears' | 'current' | 'promiseToday' | 'noContact';

export const CUSTOMER_STAT_KEYS: readonly CustomerStatKey[] = [
    'all',
    'arrears',
    'current',
    'promiseToday',
    'noContact',
];

export const CUSTOMER_STAT_LABEL: Record<CustomerStatKey, string> = {
    all: 'Todos',
    arrears: 'En mora',
    current: 'Al día',
    promiseToday: 'Compromiso hoy',
    noContact: 'Sin contacto 3d+',
};

export type CustomerStats = Record<CustomerStatKey, number>;

export type CustomerTabKey = 'summary' | 'credits' | 'contacts' | 'promises' | 'payments';

/** Las pestañas del detalle, en el orden del mockup. */
export const CUSTOMER_TABS: readonly { key: CustomerTabKey; label: string }[] = [
    { key: 'summary', label: 'Resumen' },
    { key: 'credits', label: 'Créditos' },
    { key: 'contacts', label: 'Contactos' },
    { key: 'promises', label: 'Compromisos' },
    { key: 'payments', label: 'Pagos' },
];

/** Las 7 columnas del mockup, en su orden. El candado de `CustomersPage.test.tsx` compara contra esto. */
export const CUSTOMER_COLUMN_LABELS: readonly string[] = [
    'CL',
    'Cliente',
    'Nvl',
    'DPI',
    'Mora $',
    'Compromiso',
    'Últ. cont.',
];

/** Los cinco grupos de la columna de filtros, en su orden. */
export const CUSTOMER_FILTER_GROUP_LABELS: readonly string[] = [
    'Búsqueda',
    'Nivel',
    'Días de mora',
    'Compromiso',
    'Contacto reciente',
];

export const TIER_FILTER_VALUES: readonly TierFilterValue[] = [
    ClientLevelEnum.GOLD,
    ClientLevelEnum.SILVER,
    ClientLevelEnum.BRONZE,
    NO_TIER_FILTER,
];

export const ARREARS_FILTER_OPTIONS: readonly { value: ArrearsFilter; label: string }[] = [
    { value: 'any', label: 'Todos' },
    { value: 'current', label: 'Al día' },
    { value: '1-7', label: '1–7 días' },
    { value: '8-30', label: '8–30 días' },
    { value: '31-60', label: '31–60 días' },
    { value: '61-90', label: '61–90 días' },
    { value: '90+', label: '90+ días' },
];

export const PROMISE_FILTER_OPTIONS: readonly { value: PromiseFilter; label: string }[] = [
    { value: 'any', label: 'Todos' },
    { value: 'today', label: 'Compromiso hoy' },
    { value: 'tomorrow', label: 'Compromiso mañana' },
    { value: 'active', label: 'Con compromiso activo' },
    { value: 'broken', label: 'Con incumplido' },
    { value: 'none', label: 'Sin compromiso' },
];

export const CONTACT_FILTER_OPTIONS: readonly { value: ContactFilter; label: string }[] = [
    { value: 'any', label: 'Todos' },
    { value: 'today', label: 'Contactado hoy' },
    { value: '24h', label: 'Sin contacto 24 h' },
    { value: '3d', label: 'Sin contacto 3 días' },
    { value: '7d', label: 'Sin contacto 7 días' },
];
